// M004-F0744 (design section 2: rename, notes, date range, close, delete live in the pane header's Details) - the envelope-level
// drawer, carried over from the F0636 drawer's behaviour with the receipts moved out to the workspace grid. All calls go through the
// CSLPT client. Every save re-reads its value from the answer (P-4). The retention countdown reads ONLY the server-derived
// days_remaining / purge_after / expiring. Unsorted (kind: unsorted) cannot be renamed, closed or deleted (the Ledger refuses with
// ENVELOPE_UNSORTED_PROTECTED); the controls are not offered. A locked or offline envelope is read-only.
import React, { useEffect, useRef, useState } from 'react';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';
import { envelopeApi } from '../../services/envelopeApi';
import {
  countdownText, downloadEnvelopeArchive, extractEnvelopeError, formatBytes, formatDateRange, saveBlobDownload, statusChip,
} from './envelopeModel';
import { isUnsorted, normaliseEnvelope, stateChipFor } from './envelopeWorkspaceModel';
import useDialogFocus from './useDialogFocus';

const unwrap = (response) => response?.data?.data || {};

function DeleteConfirm({ envelope, onConfirm, onCancel }) {
  const ref = useRef(null);
  useDialogFocus(ref, onCancel, { initialFocus: '[data-testid="envelope-delete-cancel"]' });
  return (
    <div className="ew-modal-backdrop" role="presentation">
      <div ref={ref} className="ew-modal" role="alertdialog" aria-modal="true" aria-labelledby="ew-delete-title" data-testid="envelope-delete-modal">
        <h3 id="ew-delete-title">
          {`Delete envelope "${envelope.name}"? Its ${envelope.counts.receipts} receipts stay for 30 days, then they are purged. You can restore it until then.`}
        </h3>
        <div className="ew-actions">
          <button type="button" className="ew-btn ew-btn-danger" data-testid="envelope-delete-confirm" onClick={onConfirm}>Delete</button>
          <button type="button" className="ew-btn" data-testid="envelope-delete-cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function EnvelopeDetailsDrawer({ orgId, envelope, readOnly = false, readOnlyReason = '', onClose, onRecord, onBusy = null }) {
  const ref = useRef(null);
  const dismiss = useDismissOnOutsideClick(onClose);
  useDialogFocus(ref, onClose);
  const [failure, setFailure] = useState(null);
  const [editing, setEditing] = useState('');
  const [drafts, setDrafts] = useState({ name: '', notes: '', from: '', to: '' });
  const [zipBusy, setZipBusy] = useState(false);
  const busyRef = useRef(null);
  busyRef.current = onBusy;
  const busyNow = zipBusy || editing !== ''; // M004-F0793 d: no refresh under an open edit or a zip in preparation
  useEffect(() => {
    const report = busyRef.current;
    if (typeof report === 'function') report(busyNow);
    return () => { if (typeof report === 'function' && busyNow) report(false); };
  }, [busyNow]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const id = envelope.envelope_id;
  const deleted = envelope.status === 'deleted';
  const closed = envelope.status === 'closed';
  const protectedEnvelope = isUnsorted(envelope);
  const frozen = readOnly || envelope.locked || deleted;
  const chip = stateChipFor(normaliseEnvelope(envelope)) || statusChip(envelope);
  const setDraft = (patch) => setDrafts((current) => ({ ...current, ...patch }));

  const apply = async (call) => {
    setFailure(null);
    try {
      const saved = unwrap(await call());
      onRecord(saved);
      return true;
    } catch (cause) {
      setFailure(extractEnvelopeError(cause));
      return false;
    }
  };

  const saveName = async () => { if (drafts.name.trim() && await apply(() => envelopeApi.update(orgId, id, { name: drafts.name.trim() }))) setEditing(''); };
  const saveNotes = async () => { if (await apply(() => envelopeApi.update(orgId, id, { notes: drafts.notes }))) setEditing(''); };
  const saveDates = async () => { if (await apply(() => envelopeApi.update(orgId, id, { date_from: drafts.from || null, date_to: drafts.to || null }))) setEditing(''); };

  const downloadZip = async () => {
    setZipBusy(true);
    setFailure(null);
    try {
      const bytes = await downloadEnvelopeArchive(orgId, id);
      saveBlobDownload(new Blob([bytes], { type: 'application/zip' }), `${envelope.name}.zip`);
    } catch (cause) {
      setFailure(cause?.code && !cause.response ? { code: cause.code, message: cause.message } : extractEnvelopeError(cause));
    } finally {
      setZipBusy(false);
    }
  };

  const edit = (field, patch) => () => { setDraft(patch); setEditing(field); };

  return (
    <div className="ew-backdrop" role="presentation" {...dismiss}>
      <div ref={ref} className="ew-drawer" role="dialog" aria-modal="true" aria-labelledby="env-drawer-title" data-testid="envelope-drawer" onClick={(event) => event.stopPropagation()}>
        <header className={`ew-drawer-head${deleted ? ' env-drawer-countdown-band' : ''}${deleted && envelope.expiring ? ' env-drawer-countdown-warning' : ''}`} data-testid="envelope-drawer-header">
          <div>
            {editing === 'name' ? (
              <span className="ew-edit-row">
                <label htmlFor="ew-name-input" className="ew-visually-hidden">Envelope name</label>
                <input id="ew-name-input" value={drafts.name} maxLength={120} data-testid="envelope-name-input" onChange={(event) => setDraft({ name: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') saveName(); }} />
                <button type="button" className="ew-btn ew-btn-primary" data-testid="envelope-name-save" onClick={saveName}>Save</button>
                <button type="button" className="ew-btn" onClick={() => setEditing('')}>Cancel</button>
              </span>
            ) : (
              <>
                <h2 id="env-drawer-title" data-testid="envelope-drawer-name" className="ew-inline-title">{envelope.name}</h2>
                {!frozen && !protectedEnvelope ? <button type="button" className="ew-icon-btn" aria-label="Rename envelope" data-testid="envelope-name-pencil" onClick={edit('name', { name: envelope.name })}>✎</button> : null}
              </>
            )}
            <p className="ew-muted" data-testid="envelope-drawer-counts">{envelope.counts.receipts} receipts · {formatBytes(envelope.counts.bytes)}</p>
            {deleted ? (
              <p className="env-countdown" data-testid="envelope-countdown">
                <span data-testid="envelope-countdown-text">{countdownText(envelope)}</span>
                {' '}
                <button type="button" className="ew-btn" disabled={readOnly} data-testid="envelope-restore" onClick={() => apply(() => envelopeApi.restore(orgId, id))}>Restore</button>
              </p>
            ) : null}
          </div>
          <div className="ew-head-actions">
            {chip.label ? <span className={`ew-chip ew-chip-${chip.tone}`} data-testid="envelope-drawer-chip">{chip.label}</span> : null}
            <button type="button" className="ew-btn" data-testid="envelope-drawer-close" onClick={onClose}>Close</button>
          </div>
        </header>

        {readOnly && readOnlyReason ? <p className="ew-notice">{readOnlyReason}</p> : null}
        {failure ? (
          <p className="ew-error" role="alert" data-testid="envelope-error">
            {failure.message}
            {failure.code ? <> <code data-testid="envelope-error-code">{failure.code}</code></> : null}
          </p>
        ) : null}

        <dl className="ew-details">
          <div>
            <dt>Notes</dt>
            <dd>
              {editing === 'notes' ? (
                <span className="ew-edit-row">
                  <label htmlFor="ew-notes-input" className="ew-visually-hidden">Envelope notes</label>
                  <textarea id="ew-notes-input" value={drafts.notes} data-testid="envelope-notes-input" onChange={(event) => setDraft({ notes: event.target.value })} />
                  <button type="button" className="ew-btn ew-btn-primary" data-testid="envelope-notes-save" onClick={saveNotes}>Save</button>
                  <button type="button" className="ew-btn" onClick={() => setEditing('')}>Cancel</button>
                </span>
              ) : (
                <span className="ew-inline">
                  {envelope.notes || '—'}
                  {!frozen ? <button type="button" className="ew-icon-btn" aria-label="Edit notes" data-testid="envelope-notes-pencil" onClick={edit('notes', { notes: envelope.notes || '' })}>✎</button> : null}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt>Date range</dt>
            <dd>
              {editing === 'dates' ? (
                <span className="ew-edit-row">
                  <input type="date" aria-label="Date from" value={drafts.from} data-testid="envelope-date-from" onChange={(event) => setDraft({ from: event.target.value })} />
                  <input type="date" aria-label="Date to" value={drafts.to} data-testid="envelope-date-to" onChange={(event) => setDraft({ to: event.target.value })} />
                  <button type="button" className="ew-btn ew-btn-primary" data-testid="envelope-dates-save" onClick={saveDates}>Save</button>
                  <button type="button" className="ew-btn" onClick={() => setEditing('')}>Cancel</button>
                </span>
              ) : (
                <span className="ew-inline">
                  {formatDateRange(envelope.date_from, envelope.date_to) || '—'}
                  {!frozen ? <button type="button" className="ew-icon-btn" aria-label="Edit date range" data-testid="envelope-dates-pencil" onClick={edit('dates', { from: envelope.date_from || '', to: envelope.date_to || '' })}>✎</button> : null}
                </span>
              )}
            </dd>
          </div>
        </dl>

        <div className="ew-actions">
          <button type="button" className="ew-btn" data-testid="envelope-drawer-zip" disabled={zipBusy || readOnly} onClick={downloadZip}>{zipBusy ? 'Preparing zip…' : 'Download zip'}</button>
          {!frozen && !protectedEnvelope ? (
            <button type="button" className="ew-btn" data-testid="envelope-drawer-toggle-closed" onClick={() => apply(() => (closed ? envelopeApi.reopen(orgId, id) : envelopeApi.close(orgId, id)))}>{closed ? 'Reopen' : 'Close envelope'}</button>
          ) : null}
          {!frozen && !protectedEnvelope ? <button type="button" className="ew-btn ew-btn-danger" data-testid="envelope-drawer-delete" onClick={() => setConfirmDelete(true)}>Delete</button> : null}
        </div>

        {confirmDelete ? (
          <DeleteConfirm
            envelope={envelope}
            onCancel={() => setConfirmDelete(false)}
            onConfirm={async () => { await apply(() => envelopeApi.delete(orgId, id)); setConfirmDelete(false); }}
          />
        ) : null}
      </div>
    </div>
  );
}
